/**
 * A client's appointments: the calls and in-person sittings booked with them.
 *
 * Their own file rather than more of `clients.types`, because nothing about
 * them is a client record — they are a list hung off one, the way payments
 * are, and that list has a wire shape of its own still to arrive. When it
 * does, the mapping in `appointmentsService` is the only thing that has to
 * learn it; everything here is already the app's own casing.
 */

/**
 * Where the appointment happens. The distinction is not decoration: an online
 * one carries a link that can be joined, and an offline one has nothing to
 * press — see `NextAppointmentBanner`, which draws the type where the Join
 * would be.
 */
export type AppointmentType = "Online" | "Offline";

/**
 * Who the appointment is with: somebody already on the books, or somebody who
 * has only enquired so far.
 *
 * `type` on the wire, and the backend's own two words kept rather than
 * translated — unlike `IN_PERSON` above, which is a word this screen has
 * always said differently. "inquiry" is spelled the backend's way here and in
 * every wire-facing name below; the screens say "Enquiry", which is this
 * app's spelling everywhere else and is only ever a label.
 *
 * It matters because the two are not interchangeable anywhere past the name
 * on the row. A client has a record, a history, a plan and a phone number to
 * put in the Meeting dialog; an enquiry has none of that yet, and a screen
 * that looked one up by the other's id would ask the clients endpoint for
 * something it has never heard of. See `clientIdOf`.
 */
export type AppointmentSubject = "client" | "inquiry";

/**
 * Who a booking is being made with — which of the two, and their `public_id`.
 *
 * One value rather than two optional ids, because exactly one of them is ever
 * set and a pair of `clientId?`/`inquiryId?` arguments is a pair somebody can
 * pass neither of. The Schedule dialog hands one of these back, and
 * `createAppointment` turns it into the single key the payload carries.
 */
export interface AppointmentAttendee {
  subject: AppointmentSubject;
  /** The client's or the enquiry's `public_id`, whichever `subject` names. */
  id: string;
}

/**
 * What was decided about the appointment.
 *
 * Three, and every one of them is written by somebody. "Completed" used to be
 * worked out from the clock — a booked appointment whose time had passed was
 * assumed to have happened — and that assumption is exactly what this screen
 * refuses to make: a client who did not turn up leaves an appointment that is
 * past and did not take place, which no reading of `startsAt` can tell from
 * one that did. So once an appointment has started the practitioner is asked,
 * and the answer is stored.
 *
 * A client who did not attend is recorded as `Cancelled`. The backend keeps
 * one state for an appointment that did not happen however it failed to, so
 * the screen keeps one too rather than showing a distinction that would not
 * survive a reload. The reason field is where the difference is written down.
 *
 * Until they answer, the appointment is still `Scheduled` and its row shows
 * the question rather than a state — see `appointmentState`.
 */
export type AppointmentStatus =
  | "Scheduled"
  | "Rescheduled"
  | "Completed"
  | "Cancelled";

/**
 * Whether the appointment is still standing: booked, and nobody has said
 * otherwise.
 *
 * `Rescheduled` is `Scheduled` in every way that matters — it is going to
 * happen, it can be joined, moved and called off, and it is asked about once
 * its time passes. The only thing it changes is the word on the pill. So the
 * two are spelled out here once rather than at each of the five places that
 * ask, because a call site written `status === "Scheduled"` after this status
 * arrived is one that quietly drops every moved appointment: off the carousel,
 * out of the Upcoming tab, or out of its own row menu. That is not a guess —
 * it is what happened the last time this screen had two words for one idea.
 */
export const isStandingStatus = (status: AppointmentStatus): boolean =>
  status === "Scheduled" || status === "Rescheduled";

/**
 * What the table's Status column reads: the stored status with the clock
 * folded in.
 *
 * One of these is not a status. `Awaiting` is a booked appointment whose time
 * has come and gone with nobody saying what happened — drawn as the two
 * answers rather than as a pill, the way an unpaid invoice is. Everything else
 * here is `status` as the backend sent it: the screen says what the record
 * says, and does not work out a state of its own on top of it.
 *
 * `Rescheduled` included. The app once inferred that word from `updated_at`
 * standing later than `created_at`, which reported a state nothing had sent;
 * the backend now has a `RESCHEDULED` of its own, so the pill reads it rather
 * than deducing it. See `isStandingStatus` for what it does not change.
 */
export type AppointmentState =
  | "Scheduled"
  | "Rescheduled"
  | "Awaiting"
  | "Completed"
  | "Cancelled";

/** One appointment, in the app's own casing. */
export interface ClientAppointment {
  /** The appointment's own id — its `public_id`, once there is a backend. */
  id: string;
  /**
   * Whether it is with a client or with an enquiry — `type` on the wire.
   *
   * Every row carries it, and it is what the four name-and-id fields below
   * are read through: a row is one or the other, so the pair that does not
   * apply is `null`. A response that names neither reads as `"client"`, which
   * is what every appointment was before enquiries could be booked.
   */
  subject: AppointmentSubject;
  /** ISO datetime it starts at. Local time is the reader's; the wire is UTC. */
  startsAt: string;
  type: AppointmentType;
  /**
   * How long it runs, in minutes. One number rather than the hours-and-minutes
   * pair the dialog collects: the two dropdowns are how the question is asked,
   * not what the answer is, and a duration that is only ever added together is
   * better off stored added together. See `durationLabel`.
   */
  durationMinutes: number;
  /**
   * Where to join an online appointment. `null` for an offline one, and also
   * for an online one booked without a link — the dialog does not insist on
   * one, so a row can be online and have nothing to press.
   */
  meetingUrl: string | null;
  /**
   * Where an offline appointment is being held. `null` for an online one, and
   * for an offline one booked without one — the dialogs ask for an address the
   * way they ask for a link, which is to say without insisting.
   *
   * The other half of `meetingUrl` rather than a reuse of it: an address is
   * not a URL, and a column that holds both is one nothing can render without
   * first asking which it got. See `AppointmentResponse.address`, which is the
   * field this reads off and is ahead of the backend.
   */
  address: string | null;
  /**
   * What the appointment is for — `meeting_agenda` on the wire, held as the
   * backend's own code (`FOLLOW_UP`) rather than as the words on screen.
   *
   * The code and not the label, because this is the stored value: it goes back
   * up on the next write, it is what a filter would one day be keyed by, and a
   * field holding "Follow-up" is one that has already thrown away which of the
   * seven it was. The words are put back by `agendaLabel`, at the four places
   * that draw it — and that reader also takes a label, because the records
   * written before the choices existed carry one.
   *
   * `null` where none was chosen, which is an ordinary answer: the dialog asks
   * but does not insist.
   */
  agenda: string | null;
  /**
   * What the appointment is about, in the practitioner's own words — up to
   * `MAX_DESCRIPTION` of them.
   *
   * The agenda's long half and kept apart from it for the reason the two
   * fields exist at all: the agenda is one of seven and is what the table
   * column and the timeline row read, and this is prose that only the Meeting
   * dialog has room for. One field holding both would be unsortable and
   * uncountable the moment anybody typed into it.
   *
   * Not to be confused with `completionNote`, which is written afterwards and
   * says what happened. This is written when the appointment is booked and
   * says what it is to be about.
   */
  description: string | null;
  status: AppointmentStatus;
  /**
   * Who it is with, where the backend named them — `client.name` off the wire.
   *
   * Read for one thing: the clash a dialog reports when the slot being booked
   * is already taken. "Sameer already has an appointment at 11:00" is a
   * sentence somebody can act on; "this slot is taken" leaves them hunting
   * through the diary for what took it.
   *
   * Optional, and absent rather than null on most rows: only the reads that
   * span clients ask for it. A row off a client's own list has nobody to name
   * that the screen does not already know.
   */
  clientName?: string | null;
  /**
   * Whose it is — `client.public_id` off the wire.
   *
   * The sibling of `clientName` above and present on the same rows: the reads
   * that span clients. My task is why it is here — the day's diary is a list
   * of other people's appointments, and everything that screen offers to do
   * about one needs the client it belongs to, from looking up their number for
   * the Meeting dialog to booking the next one.
   */
  clientId?: string | null;
  /**
   * Who it is with where the appointment was booked against an enquiry
   * instead — `inquiry.name` off the wire, and `null` on every row that has a
   * client.
   *
   * Its own field rather than more of `clientName`, for the reason this file
   * keeps `address` and `meetingUrl` apart: a column holding either is one
   * nothing can act on without first asking which it got, and the two ids
   * below point at different endpoints. The name alone could have been
   * shared; the id could not, and splitting one while merging the other would
   * be the worse of both. See `attendeeName`, which is what the screens that
   * only want the words read.
   */
  inquiryName?: string | null;
  /**
   * The enquiry's `public_id`, and the twin of `clientId` above — `null`
   * wherever that one is set.
   *
   * Never passed to anything that takes a client: an enquiry has no client
   * record behind it, which is the whole reason this is a second field. The
   * Meeting dialog reads `clientId` for the contact line and so asks nothing
   * on an enquiry's row, and `invalidateAppointmentReads` skips the client
   * history for the same reason.
   */
  inquiryId?: string | null;
  /**
   * What the practitioner wrote down when they marked the session as having
   * taken place — `completion_note` on the wire. `null` where they wrote
   * nothing, which is the ordinary case: the box is optional.
   *
   * The twin of `cancelReason` below, and kept apart from it for the reason
   * that one gives for holding both halves of a cancellation in one field: a
   * row is only ever one of these. An appointment that was completed was never
   * called off, and one that was called off never came round to be noted. Two
   * fields because the two are written at different moments by different
   * dialogs, and a single `note` column would make "why it did not happen" and
   * "what happened" indistinguishable on a row that has one of them.
   */
  completionNote: string | null;
  /**
   * Why it was called off, or why the client did not attend — whichever of the
   * two dialogs was answered. Both mark the field optional, so `null` is an
   * ordinary answer rather than a gap.
   *
   * One field for both because a row is only ever one of them: an appointment
   * that was cancelled never came round to be missed, and one marked a no-show
   * was never called off. The Cancelled tab is the only place it is read.
   */
  cancelReason: string | null;
}

/** What the Schedule dialog collects; the id and the status are not its to set. */
export interface NewClientAppointment {
  startsAt: string;
  type: AppointmentType;
  durationMinutes: number;
  /** One of `MEETING_AGENDAS`, by code. `null` where none was chosen. */
  agenda: string | null;
  /** Up to `MAX_DESCRIPTION` characters, and `null` where the box was left
   * empty — the same reading every optional box on these dialogs gets. */
  description: string | null;
  /** Online only; `null` on an offline one, whatever the dialog was holding. */
  meetingUrl: string | null;
  /** Offline only, and the mirror of the above. */
  address: string | null;
}

/**
 * What the Reschedule dialog collects. A new day, a new time and a new length
 * — the type and the link are not up for discussion there, which is why this
 * is not just `NewClientAppointment` again.
 */
export interface RescheduleAppointment {
  startsAt: string;
  durationMinutes: number;
  /**
   * Where an offline one is now being held, where that has changed with it.
   * Moving an in-person sitting to another day often moves the room as well,
   * so the dialog asks — and `undefined` where it did not, which is every
   * online appointment: a field left off is a field the backend leaves alone.
   */
  address?: string | null;
}

/**
 * The two answers an appointment wants once its time has passed: the session
 * took place, or it did not. Named as the acts rather than as the statuses
 * they write, because that is what the dialogs ask — and because the second
 * one is the same act as calling off an appointment that is still ahead, which
 * is why both go to the same endpoint.
 */
export type AppointmentAnswer = "completed" | "cancelled";

/** The inner tabs over a client's appointment table, in the order drawn. */
export type AppointmentTabId = "all" | "upcoming" | "past" | "cancelled";

/* -------------------------------------------------------------------------
 * The wire.
 *
 * snake_case exactly as the backend sends it, and its enum constants rather
 * than this screen's words. Only `appointmentsService` touches anything below:
 * the casing, the `IN_PERSON`, and the fact that a length arrives as two
 * moments rather than as a number of minutes all stop at that boundary.
 * ---------------------------------------------------------------------- */

/** Where it happens, as the backend spells it. `IN_PERSON`, not `OFFLINE`. */
export type AppointmentTypeCode = "ONLINE" | "IN_PERSON";

/**
 * Where the appointment stands against the clock, as the backend read it when
 * it answered. See `AppointmentResponse.temporal_state` for why nothing reads
 * it.
 */
export type AppointmentTemporalState = "UPCOMING" | "IN_PROGRESS" | "PAST";

/** What was decided about it, as the backend spells it. */
export type AppointmentStatusCode =
  | "SCHEDULED"
  | "RESCHEDULED"
  | "CANCELLED"
  | "COMPLETED";

/** Either party on an appointment, named. The practitioner carries no id. */
export interface AppointmentPartyResponse {
  public_id?: string;
  name?: string;
}

/** How one appointment's link was made, as the record reports it. */
export interface AppointmentMeetingResponse {
  provider?: MeetingProviderCode | string;
  /** `READY` once a link exists, `NONE` where none was made. */
  status?: string;
  error_code?: string | null;
}

/** One appointment, whole — a row of the list, and what every write answers with. */
export interface AppointmentResponse {
  public_id: string;
  /**
   * Which of the two the appointment is with: `"client"` or `"inquiry"`.
   *
   * It says which of the two fields under it is filled in — the other comes
   * back `null`. Optional here rather than required because it is newer than
   * the records it describes, and one that arrives without it is read as a
   * client's. See `toSubject`, which also takes an embedded `inquiry` as an
   * answer where the field itself is missing.
   */
  type?: AppointmentSubject | string;
  /** `null` on an appointment booked against an enquiry. */
  client?: AppointmentPartyResponse | null;
  /**
   * Whose enquiry it is, where the appointment was booked against one —
   * `{ public_id, name }`, the same shape as `client`, and `null` on every
   * appointment with a client.
   */
  inquiry?: AppointmentPartyResponse | null;
  practitioner?: AppointmentPartyResponse;
  /** ISO datetimes carrying an offset: `2026-09-20T10:00:00+05:30`. */
  starts_at: string;
  /** The length, as the far end of it. There is no `duration_minutes` here. */
  ends_at?: string | null;
  /** The zone the appointment was booked in, e.g. `Asia/Kolkata`. */
  timezone?: string;
  appointment_type: AppointmentTypeCode;
  meeting_url?: string | null;
  /**
   * Where an in-person one happens.
   *
   * Ahead of the backend: the dialogs collect it and both writes send it, and
   * a record that comes back without it reads as `null` rather than as an
   * error. Declared here because it is what this side sends and what it will
   * be given back, and a field the app writes but does not admit to reading is
   * the harder of the two to find later.
   */
  address?: string | null;
  /**
   * What the appointment is for, as one of the backend's `MeetingAgenda`
   * codes — `FOLLOW_UP`, `DIET_PLAN_REVIEW` and the five others.
   *
   * `""` rather than `null` where none was chosen, which is the shape every
   * optional string on this record takes. Read through `agendaLabel`, which
   * also accepts the human label: the field was free text before it was a
   * choice, and the rows written then carry "Follow-up" rather than the code.
   */
  meeting_agenda?: string | null;
  /**
   * What it is about, in the practitioner's own words. `""` where the box was
   * left empty.
   *
   * The booking's own description and not the completion's — see
   * `completion_note`, which is written afterwards and answers a different
   * question.
   */
  description?: string | null;
  status: AppointmentStatusCode;
  /**
   * The backend's own reading of the clock: `UPCOMING`, `IN_PROGRESS` or
   * `PAST`.
   *
   * Not read, on any screen. It is a snapshot taken when the list was fetched,
   * and these screens stay open across the moment an appointment starts: a
   * strip has to stop offering a way in and start asking what happened, and My
   * task's timeline has to move its highlight down the day — all without
   * anybody reloading. So the clock is read locally, once a minute — see
   * `hasStarted` and `isInProgress`. Declared here because it is on the wire,
   * and a field the app silently ignores is better written down than
   * discovered twice.
   */
  temporal_state?: AppointmentTemporalState | string;
  cancelled_at?: string | null;
  /** `""` rather than `null` where none was given. */
  cancellation_reason?: string | null;
  /** When the session was marked as having taken place; `null` until it is. */
  completed_at?: string | null;
  /** `""` rather than `null` where the session was marked without a note. */
  completion_note?: string | null;
  /**
   * How the meeting link for this appointment was made, and whether it is
   * good: `{ provider, status, error_code }`, e.g. `GOOGLE_MEET` / `READY`.
   *
   * Not read. The screens ask `meetingUrl` whether there is a link to press,
   * which is the only question any of them puts — and `meeting-settings/`
   * answers "which app is making links" for the workspace, which is a
   * different question from "which app made this one". Declared because it is
   * on the wire, and a field the app silently ignores is better written down
   * than discovered twice.
   */
  meeting?: AppointmentMeetingResponse;
  created_at?: string;
  updated_at?: string;
}

/** What books one. The length goes up in minutes and comes back as `ends_at`. */
export interface CreateAppointmentPayload {
  /**
   * The client's `public_id`, on an appointment with a client. Left off
   * entirely — not sent `null` — where `inquiry` is the one being sent.
   */
  client?: string;
  /**
   * The enquiry's `public_id`, on one booked against an enquiry, and the
   * mirror of `client` above: exactly one of the two goes up, and which one
   * is what the response comes back typed as. See `AppointmentAttendee`,
   * which is how the dialog says which it has.
   */
  inquiry?: string;
  starts_at: string;
  duration_minutes: number;
  appointment_type: AppointmentTypeCode;
  /** Left off entirely for an in-person one, rather than sent empty. */
  meeting_url?: string;
  /** And left off for an online one — the same rule, the other way round. */
  address?: string;
  /**
   * One of the agenda codes. Left off where none was chosen rather than sent
   * as `""`: a choice field is being asked to store one of seven values, and
   * an empty string is not one of them.
   */
  meeting_agenda?: string;
  /** Left off where nothing was written, by the same rule. */
  description?: string;
}

/** What moves one: a new slot and a new length, and nothing else. */
export interface RescheduleAppointmentPayload {
  starts_at: string;
  duration_minutes: number;
  /** The room, where an offline one moved to a new one. Left off otherwise. */
  address?: string;
}

/**
 * What says one took place. `""` where the practitioner wrote nothing, which
 * is the ordinary case — the box is optional, and a session that happened
 * happened whether or not there was time to write about it.
 */
export interface CompleteAppointmentPayload {
  note: string;
}

/** What calls one off. `""` where the practitioner gave no reason. */
export interface CancelAppointmentPayload {
  reason: string;
}

/* ---------------------------------------------------------------------------
 * Meeting settings: how this workspace makes the link for an online one.
 * ------------------------------------------------------------------------ */

/**
 * What makes the link. One of them at a time: this is a single field on the
 * workspace rather than a set of switches, so turning Zoom on is what turns
 * Google Meet off. `MANUAL` is where a workspace starts, and what it goes
 * back to when either is turned off.
 */
export type MeetingProviderCode = "GOOGLE_MEET" | "ZOOM" | "MANUAL";

/**
 * How a provider is addressed in a URL — the `slug` the backend names it by,
 * and the word that goes into `/meeting-providers/{slug}/connect/` and into
 * this app's own `/integrations/{slug}/callback` route.
 *
 * A separate name from `MeetingProviderCode` because they are two different
 * names for the same thing on two different parts of the wire: a slug
 * addresses a provider in a path, a code says which one is in use.
 */
export type MeetingProviderSlug = "google" | "zoom";

/** Whether a linked account is still good for making links. */
export type MeetingConnectionStatus = "ACTIVE" | string;

/** A linked account, as the backend describes it. `null` until one is. */
export interface MeetingConnectionResponse {
  status: MeetingConnectionStatus;
  account_email?: string | null;
  connected_at?: string | null;
}

/** One provider the backend knows about. */
export interface MeetingProviderResponse {
  provider: MeetingProviderCode | string;
  /** How the provider is named in a URL — `google`, `zoom`. */
  slug: string;
  label: string;
  /**
   * Whether this provider can be used. It tracks the connection rather than
   * the build: it comes back `false` with a `null` connection, and `true`
   * once an account is linked.
   */
  available: boolean;
  connection?: MeetingConnectionResponse | null;
}

/**
 * What `GET` and `PATCH /meeting-settings/` both answer with.
 *
 * The `*_enabled` flags restate `meeting_provider` one provider at a time,
 * and the backend sends only the ones it has something to say about: the
 * `GOOGLE_MEET` body carries no `zoom_enabled` at all rather than carrying it
 * `false`. So a missing flag is no answer rather than a no — see
 * `toMeetingSettings`, which falls back to the provider code.
 */
export interface MeetingSettingsResponse {
  meeting_provider: MeetingProviderCode | string;
  google_meet_enabled?: boolean;
  zoom_enabled?: boolean;
  providers: MeetingProviderResponse[];
}

/** What `PATCH /meeting-settings/` sends — the whole payload. */
export interface MeetingSettingsPayload {
  meeting_provider: MeetingProviderCode;
}

/**
 * What `POST /meeting-providers/{slug}/connect/` answers with — the provider's
 * own sign-in URL, for either provider. One shape because it is one call with
 * a slug in it; see `startProviderConnect`.
 */
export interface ProviderConnectResponse {
  authorization_url: string;
}

/**
 * The meeting settings in this app's words: which apps exist, which one is
 * making links, and whose account each is linked to.
 */
export interface MeetingSettings {
  /** What is making the links right now. */
  provider: MeetingProviderCode;
  /** Whether Google Meet is the one making them. */
  googleMeetEnabled: boolean;
  /** Whether Zoom is. Never both — `provider` holds one answer. */
  zoomEnabled: boolean;
  /** Every provider the backend named, by slug. */
  providers: MeetingProviderLink[];
}

/** One provider, converted. */
export interface MeetingProviderLink {
  provider: MeetingProviderCode | string;
  slug: string;
  label: string;
  available: boolean;
  /**
   * Whether an account is linked and still good — the difference between
   * "sign in to Google" and "switch it back on", which are different presses.
   */
  linked: boolean;
  /** Whose account, where the backend named one. */
  accountEmail: string | null;
}
